<script setup lang="ts">
import GenericChart from '@/composables/charts/GenericChart.vue'
import CompiledChartTimeSetupPicker from '@/components/CompiledChartTimeSetupPicker.vue'
import { computed, onBeforeMount, ref, Ref } from 'vue'
import Highcharts from 'highcharts'
import GenericCustomDialog from '@/components/GenericCustomDialog.vue'
import { humanReadableTimestamp } from '@/composables/datetime'
import { useInstanceStore } from '@/store/backendInstances'
import { useRouter } from 'vue-router'
import { TimeSeriesChartBuilder } from '@/composables/charts/influx-charts/chart-builders/TimeSeriesChartBuilder'
const router = useRouter()

const props = withDefaults(
  defineProps<{
    chartBuilder: TimeSeriesChartBuilder
    enableClickEvent?: boolean
  }>(),
  {
    enableClickEvent: true,
  }
)

const showDataPointDialog: Ref<boolean> = ref(false)
const dataPointDialogX: Ref<number> = ref(0)
function onDataPointClick(dataPoint: Highcharts.PointerEventObject): void {
  const point = (dataPoint as any).point
  if (!point?.options?.x) return
  dataPointDialogX.value = point.options.x
  showDataPointDialog.value = true
}

const canJumpToCanvas = computed(() => {
  if (!showDataPointDialog.value) return false
  return useInstanceStore().hasAnyPermission(['cameraCanvases.view', 'cameraCanvases.view.basic'])
})

function jumpToCanvas() {
  return router.push({ name: 'CameraCanvas', query: { timestamp: dataPointDialogX.value } })
}

onBeforeMount(() => {
  props.chartBuilder.attachDataPointClickHandler(onDataPointClick)
})
defineSlots<{
  aboveChartColumns: unknown
  belowChartColumns: unknown
  dataPointClickDialogActions: {
    timestamp: number
    close: () => void
  }
}>()
</script>

<template>
  <generic-custom-dialog v-model="showDataPointDialog">
    <template #headerIcon>
      <v-icon icon="mdi-timeline-clock-outline"></v-icon>
    </template>
    <template #title>
      {{ humanReadableTimestamp(dataPointDialogX) }}
    </template>
    <template #body>
      <v-btn
        v-if="canJumpToCanvas"
        block
        class="my-2"
        prepend-icon="mdi-artboard"
        append-icon="mdi-arrow-right-thin"
        variant="outlined"
        @click="jumpToCanvas"
      >
        {{ $t('charts.go_to_canvas') }}
      </v-btn>
      <slot
        name="dataPointClickDialogActions"
        :timestamp="dataPointDialogX"
        :close="
          () => {
            showDataPointDialog = false
          }
        "
      ></slot>
    </template>
    <template #actions> </template>
  </generic-custom-dialog>
  <v-container
    class="ma-0 pa-0"
    fluid
  >
    <GenericChart :chart-builder="chartBuilder">
      <template #aboveChartColumns>
        <slot name="aboveChartColumns"></slot>
        <v-col cols="auto">
          <CompiledChartTimeSetupPicker :chart-builder="chartBuilder"></CompiledChartTimeSetupPicker>
        </v-col>
      </template>
      <template
        v-if="$slots.belowChartColumns"
        #belowChartColumns
      >
        <slot name="belowChartColumns"> </slot>
      </template>
    </GenericChart>
  </v-container>
</template>

<style scoped></style>
